import React from 'react';
import { ChevronDown, Camera, Mic, Volume2, TestTube2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { DeviceInfo, DeviceTestResult } from '@/hooks/video-call/useDeviceEnumeration';

interface DeviceSelectorProps {
  label: string;
  devices: DeviceInfo[];
  selectedDeviceId?: string;
  onDeviceChange: (deviceId: string) => void;
  disabled?: boolean;
  icon?: 'camera' | 'microphone' | 'speaker';
  placeholder?: string;
  showTestButton?: boolean;
  isTestingDevice?: boolean;
  onTestDevice?: () => Promise<unknown>;
  testResult?: DeviceTestResult | null;
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  label,
  devices,
  selectedDeviceId,
  onDeviceChange,
  disabled = false,
  icon = 'camera',
  placeholder = 'Select device...',
  showTestButton = false,
  isTestingDevice = false,
  onTestDevice,
  testResult,
}) => {
  const getIcon = () => {
    switch (icon) {
      case 'camera':
        return <Camera className="w-4 h-4 text-muted-foreground dark:text-ink-faint" />;
      case 'microphone':
        return <Mic className="w-4 h-4 text-muted-foreground dark:text-ink-faint" />;
      case 'speaker':
        return <Volume2 className="w-4 h-4 text-muted-foreground dark:text-ink-faint" />;
      default:
        return <Camera className="w-4 h-4 text-muted-foreground dark:text-ink-faint" />;
    }
  };

  const selectedDevice = devices.find(device => device.deviceId === selectedDeviceId);

  const handleTestDevice = async () => {
    if (onTestDevice) {
      await onTestDevice();
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground flex items-center gap-2">
        {getIcon()}
        {label}
      </label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <select
            value={selectedDeviceId || ''}
            onChange={(e) => {
              if (e.target.value) {
                onDeviceChange(e.target.value);
              }
            }}
            disabled={disabled || devices.length === 0}
            className="w-full appearance-none bg-card border border-border rounded-lg px-3 py-2 pr-8 text-sm text-foreground dark:text-ink-faint focus:outline-none focus:ring-2 focus:ring-[#8437BB] focus:border-transparent disabled:bg-muted dark:disabled:bg-muted disabled:text-muted-foreground dark:disabled:text-ink-faint disabled:cursor-not-allowed"
          >
            <option value="" disabled>
              {devices.length === 0 ? 'No devices found' : placeholder}
            </option>
            {devices.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-ink-faint dark:text-muted-foreground pointer-events-none" />
        </div>
        
        {showTestButton && selectedDeviceId && (
          <Button
            onClick={handleTestDevice}
            disabled={disabled || isTestingDevice || !selectedDeviceId}
            variant="outline"
            size="sm"
            className="h-8 px-3 text-xs bg-card border-border text-foreground hover:bg-muted hover:text-foreground rounded-xl whitespace-nowrap"
          >
            {isTestingDevice ? (
              <>
                <LoadingSpinner className="w-3 h-3 mr-1" />
                Testing...
              </>
            ) : (
              <>
                <TestTube2 className="w-3 h-3 mr-1" />
                Test
              </>
            )}
          </Button>
        )}
      </div>
      
      {devices.length === 0 && (
        <p className="text-xs text-muted-foreground dark:text-ink-faint">
          No devices available. Please check your connections and refresh the page.
        </p>
      )}
      
      {selectedDevice && !testResult && (
        <p className="text-xs text-good">
          Selected: {selectedDevice.label}
        </p>
      )}
      
      {testResult && (
        <p className={`text-xs ${testResult.success ? 'text-good' : 'text-danger'}`}>
          {testResult.success ? (
            <>
              ✓ Test successful {testResult.volume && `(Volume: ${Math.round(testResult.volume)})`}
            </>
          ) : (
            <>
              ✗ {testResult.error}
            </>
          )}
        </p>
      )}
    </div>
  );
};